import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Divider } from '@nextui-org/react';
import { TabIntro } from '../aiAccess/shared';
import { useEnrichmentClient } from './api';
import { LookupSection } from './LookupSection';
import { StoredProductsSection } from './StoredProductsSection';
import { BulkLookupSection } from './BulkLookupSection';
import { ImportSection } from './ImportSection';

/**
 * Enriched data: products and OEM warranties by serial number, from Service Data Enrichment
 * (part-enrichment-service) and stored in the tenant's SOR records. Look up a serial, browse what
 * is stored, look a customer's list up in bulk, and import answers enriched elsewhere.
 */
export function EnrichedDataTab({ baseUrl }: { readonly baseUrl: string }) {
  const { t } = useTranslation();
  const client = useEnrichmentClient(baseUrl);
  // Bumped when a lookup, bulk run or import stored something, so the lists below load again.
  const [version, setVersion] = useState(0);
  const changed = useCallback(() => setVersion((current) => current + 1), []);

  return (
    <div className="flex flex-col gap-6 px-6 pb-6">
      <TabIntro title={t('Enriched data')}>
        <span className="text-sm text-default-500">
          {t('Products and OEM warranties by serial number, stored for your tenant. A serial is asked of the OEM’s site once, then served from the stored answer.')}
        </span>
      </TabIntro>
      <LookupSection client={client} onStored={changed} />
      <Divider />
      <StoredProductsSection client={client} version={version} onStored={changed} />
      <Divider />
      <BulkLookupSection client={client} onFinished={changed} />
      <Divider />
      <ImportSection client={client} version={version} onImported={changed} />
    </div>
  );
}

export default EnrichedDataTab;
